/* eslint-disable @typescript-eslint/consistent-type-assertions */

import { logger } from '@garnish/logger';
import * as Application from 'expo-application';
import Constants from 'expo-constants';
import { setup } from 'xstate5';

import {
  type Context,
  type Event,
  fetchUpdateIfAvailable,
  Guards,
  type Input,
  restartApp,
} from './model';

// ─── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_POLLING_INTERVAL = 20 * 60 * 1000; // 20 minutes
const NATIVE_APP_VERSION = Application.nativeApplicationVersion ?? '-';
const BUNDLE_APP_VERSION = Constants.expoConfig?.version ?? '-';

// ─── Logger ──────────────────────────────────────────────────────────────────

logger.enable('OTA_MACHINE');

const defaultLogger = logger.extend('OTA_MACHINE');

// ─── Model ───────────────────────────────────────────────────────────────────

const model = setup({
  types: {
    context: {} as Context,
    events: {} as Event,
    input: {} as Input | undefined,
  },
  actors: {
    'fetch-update-if-available': fetchUpdateIfAvailable,
    'restart-app': restartApp,
  },
  guards: {
    'check-if-updates-enabled': Guards.checkIfUpdatesEnabled,
    'check-if-new-update-is-not-available':
      Guards.checkIfNewUpdateIsNotAvailable,
  },
  delays: {
    POLLING_INTERVAL: DEFAULT_POLLING_INTERVAL,
  },
});

// ─── Machine ─────────────────────────────────────────────────────────────────

/**
 * A state machine designed for managing OTA (over-the-air) updates in an
 * application.
 *
 * Machine transitions through various states from setup and polling to the
 * installation of updates, managing the update's process flow based on async
 * events and guards.
 *
 * States:
 * - setup: Transitional state used to check if OTA updates are enabled.
 * - idle: Initial state waiting for triggers to begin polling or handle updates.
 * - polling: State that periodically checks for available updates.
 * - fetching-update-if-available: Async state that uses a service to determine if an update exists.
 * - has-update: Indicates an available update ready to be applied; can transition to restarting.
 * - restarting: Manages the restart necessary to apply the OTA update.
 * - restart-failed: Indicates a failure during restart, requiring manual intervention.
 * - disabled: Final state if OTA updates are not enabled.
 * - done: Final state indicating the successful completion of the update process.
 *
 * Transitions between states are handled based on events such as `start`,
 * `stop`, `update`, and through invocation of external services to check and
 * apply updates, accompanied by the error handling and state guards.
 */
export const otaMachine = model.createMachine({
  initial: 'setup',
  context: ({ input }) => ({
    logger: input?.logger ?? defaultLogger,
  }),
  states: {
    setup: {
      description:
        'The setup state serves as a transitional state used to determine if OTA updates are enabled.',
      entry: ({ context }) => {
        context.logger.debug(
          `Native app version: ${NATIVE_APP_VERSION} | Bundle app version: ${BUNDLE_APP_VERSION}`,
        );
      },
      always: [
        {
          target: 'idle',
          guard: 'check-if-updates-enabled',
        },
        {
          target: 'disabled',
        },
      ],
    },
    idle: {
      description:
        'Serves as the initial state of the OTA update state machine. In this state, the machine is waiting for triggers to start polling for updates or to handle initial update.',
      on: {
        start: 'polling',
        'notify-about-update': 'has-update',
      },
    },
    polling: {
      description:
        'A simple polling state that checks for a possible update based on the configurable time interval.',
      after: {
        POLLING_INTERVAL: 'fetching-update-if-available',
      },
      on: {
        stop: 'idle',
        'notify-about-update': 'has-update',
      },
    },
    'fetching-update-if-available': {
      description:
        'An async state that uses the associated service to check for an update. If no update is found, polling is triggered.',
      invoke: {
        src: 'fetch-update-if-available',
        onDone: [
          {
            target: 'polling',
            guard: {
              type: 'check-if-new-update-is-not-available',
              params: ({ event }) => ({ updateResult: event.output }),
            },
          },
          {
            target: 'has-update',
          },
        ],
        onError: {
          target: 'polling',
          actions: [({ event, context }) => context.logger.error(event.error)],
        },
      },
      on: {
        stop: 'idle',
        'notify-about-update': 'has-update',
      },
    },
    'has-update': {
      description:
        'A state indicating that there is an update available to be installed. In this state, the machine is ready to initiate the update process, potentially restarting the application to apply the new updates. Transitions to the "restarting" state when an update is triggered.',
      on: {
        stop: 'idle',
        update: 'restarting',
      },
    },
    restarting: {
      description:
        'Manages the process of restarting the application to apply an OTA update. Upon successful completion of the restart process, the state transitions to the "done" state, indicating the update process has been successfully completed. If an error occurs during the restart, it transitions to the "restart-failed" state.',
      invoke: {
        src: 'restart-app',
        onDone: {
          target: 'done',
        },
        onError: {
          target: 'restart-failed',
          actions: [({ event, context }) => context.logger.error(event.error)],
        },
      },
    },
    'restart-failed': {
      description:
        'A state that notifies the user that a manual restart is necessary.',
    },
    disabled: {
      description:
        'A state indicating that the OTA update is disabled for the current environment. It acts as a final state, and no further actions are taken beyond reaching this state.',
      type: 'final',
    },
    done: {
      description:
        'The final state, indicating that the OTA update process has been successfully completed.',
      type: 'final',
    },
  },
});
